import {
  BLUR,
  BUSY_ON_TEMPORARY,
  CANCEL,
  CHANGE,
  CLEAR_CANCEL,
  CLEAR_SUBMIT,
  CUSTOM_VALIDATION,
  DESTROY,
  FOCUS,
  FORCE_RELOAD,
  INITIALIZE,
  LOAD_DATA,
  MERGE_CONFLICTS,
  prefix,
  REGISTER_FIELD,
  RESET,
  SET_ROW_INDEX,
  SET_SUBMIT_FAILED,
  SET_SUBMIT_SUCCEEDED,
  START_SUBMIT,
  START_VALIDATION,
  STOP_SUBMIT,
  STOP_VALIDATION,
  SUBMIT,
  TEMPORARY_SAVE_SUCCEEDED,
  UNREGISTER_FIELD,
  UPDATE_MUTATION_ID,
} from './actionTypes';

import deleteIn from './deleteIn';
import deleteInWithCleanUp from './deleteInWithCleanUp';
import deepEqual from './deepEqual';
import getIn from './getIn';
import setIn from './setIn';
import mergeTemporarySaveResult from './mergeTemporarySaveResult';

const isFormAction = (action) =>
  action && action.type && action.type.length > prefix.length && action.type.substring(0, prefix.length) === prefix;

const empty = {};

const createFormReducer = () => {
  const behaviors = {
    [BLUR](state, { meta: { field, touch, mutationId }, payload }) {
      let result = state;
      const initial = getIn(result, `initial.${field}`);
      if (initial === undefined && payload === '') {
        result = deleteInWithCleanUp(result, `values.${field}`);
      } else if (payload !== undefined) {
        result = setIn(result, `values.${field}`, payload);
      }
      if (field === getIn(result, 'active')) {
        result = deleteIn(result, 'active');
      }
      if (getIn(result, `fields.${field}.active`)) {
        result = deleteIn(result, `fields.${field}.active`);
      }
      if (touch) {
        result = setIn(result, `fields.${field}.touched`, true);
        result = setIn(result, 'anyTouched', true);
      }
      if (mutationId) {
        result = setIn(result, 'mutationId', mutationId);
      }
      return result;
    },
    [MERGE_CONFLICTS](state, { payload }) {
      let result = state;
      result = setIn(result, `conflicts`, payload);
      return result;
    },
    [CANCEL](state) {
      return setIn(state, 'triggerCancel', true);
    },
    [CHANGE](state, { meta: { field, touch, mutationId }, payload }) {
      let result = state;
      const initial = getIn(result, `initial.${field}`);
      if (initial === undefined && payload === '') {
        result = deleteInWithCleanUp(result, `values.${field}`);
      } else if (payload !== undefined) {
        if (field === false) {
          result = setIn(result, `values`, payload);
        } else {
          result = setIn(result, `values.${field}`, payload);
        }
      }

      if (touch) {
        result = setIn(result, `fields.${field}.touched`, true);
        result = setIn(result, 'anyTouched', true);
      }
      if (mutationId) {
        result = setIn(result, 'mutationId', mutationId);
      }
      return result;
    },
    [CLEAR_CANCEL](state) {
      return deleteIn(state, 'triggerCancel');
    },
    [CLEAR_SUBMIT](state) {
      return deleteIn(state, 'triggerSubmit');
    },
    [FOCUS](state, { meta: { field } }) {
      let result = state;
      const previouslyActive = getIn(state, 'active');
      const focusValue = getIn(state, `values.${field}`);
      result = deleteIn(result, `fields.${previouslyActive}.active`);
      result = setIn(result, `fields.${field}.visited`, true);
      result = setIn(result, `fields.${field}.active`, true);
      result = setIn(result, `fields.${field}.focusValue`, focusValue);
      result = setIn(result, 'active', field);
      return result;
    },
    [INITIALIZE](state, { payload, describe }) {
      let result = empty;

      const registeredFields = getIn(state, 'registeredFields');
      if (registeredFields) {
        result = setIn(result, 'registeredFields', registeredFields);
      }
      result = setIn(result, 'values', payload);
      result = setIn(result, 'initial', payload);
      result = setIn(result, 'meta', describe);
      return result;
    },
    [REGISTER_FIELD](state, { payload: { dataid, type, tabId } }) {
      const key = `registeredFields['${dataid}']`;
      let field = getIn(state, key);
      if (field) {
        const count = getIn(field, 'count') + 1;
        field = setIn(field, 'count', count);
      } else {
        field = { dataid, type, tabId, count: 1 };
      }
      return setIn(state, key, field);
    },
    [RESET](state) {
      let result = empty;
      const registeredFields = getIn(state, 'registeredFields');
      if (registeredFields) {
        result = setIn(result, 'registeredFields', registeredFields);
      }
      const values = getIn(state, 'initial');
      if (values) {
        result = setIn(result, 'values', values);
        result = setIn(result, 'initial', values);
      }
      return result;
    },
    [FORCE_RELOAD](state) {
      let result = empty;
      const registeredFields = getIn(state, 'registeredFields');
      if (registeredFields) {
        result = setIn(result, 'registeredFields', registeredFields);
      }
      const values = null;
      result = setIn(result, 'values', values);
      result = setIn(result, 'initial', values);
      return result;
    },
    [START_SUBMIT](state) {
      return setIn(state, 'submitting', true);
    },
    [SUBMIT](state) {
      return setIn(state, 'triggerSubmit', true);
    },
    [STOP_SUBMIT](state, { payload }) {
      let result = state;
      result = deleteIn(result, 'submitting');
      result = deleteIn(result, 'submitFailed');
      result = deleteIn(result, 'submitSucceeded');
      if (payload && Object.keys(payload).length) {
        const { error, ...validationErrors } = payload;
        if (error) {
          result = setIn(result, 'error', error);
        } else {
          result = deleteIn(result, 'error');
        }
        if (Object.keys(validationErrors).length && validationErrors[0]) {
          result = setIn(result, 'submitErrors', validationErrors[0].message);
        } else {
          result = deleteIn(result, 'submitErrors');
        }
        result = setIn(result, 'submitFailed', true);
      } else {
        //result = setIn(result, 'submitSucceeded', true);
        result = deleteIn(result, 'error');
        result = deleteIn(result, 'submitErrors');
      }
      return result;
    },
    [SET_SUBMIT_FAILED](state, { meta: { fields } }) {
      let result = state;
      result = setIn(result, 'submitFailed', true);
      result = deleteIn(result, 'submitSucceeded');
      result = deleteIn(result, 'submitting');
      fields.forEach((field) => (result = setIn(result, `fields.${field}.touched`, true)));
      if (fields.length) {
        result = setIn(result, 'anyTouched', true);
      }
      return result;
    },
    [SET_SUBMIT_SUCCEEDED](state) {
      let result = state;
      result = deleteIn(result, 'submitFailed');
      result = setIn(result, 'submitSucceeded', true);
      return result;
    },
    [LOAD_DATA](state) {
      return setIn(state, 'loadingData', true);
    },
    [UNREGISTER_FIELD](state, { payload: { dataid, destroyOnUnmount } }) {
      let result = state;
      const key = `registeredFields['${dataid}']`;
      let field = getIn(result, key);
      if (!field) {
        return result;
      }

      const count = getIn(field, 'count') - 1;
      if (count <= 0 && destroyOnUnmount) {
        result = deleteIn(result, key);
        if (deepEqual(getIn(result, 'registeredFields'), empty)) {
          result = deleteIn(result, 'registeredFields');
        }
      } else {
        field = setIn(field, 'count', count);
        result = setIn(result, key, field);
      }
      return result;
    },
    [START_VALIDATION](state, { meta: { field } }) {
      return setIn(state, 'validating', field || true);
    },
    [STOP_VALIDATION](state, { payload }) {
      let result = state;
      result = deleteIn(result, 'validating');
      if (payload && Object.keys(payload).length) {
        const { _error, ...fieldErrors } = payload;
        if (_error) {
          result = setIn(result, 'error', _error);
        }
        if (Object.keys(fieldErrors).length) {
          result = setIn(result, 'validationErrors', fieldErrors);
        } else {
          result = deleteIn(result, 'validationErrors');
        }
      } else {
        result = deleteIn(result, 'error');
        result = deleteIn(result, 'validationErrors');
      }
      return result;
    },
    [SET_ROW_INDEX](state, { meta: { form, field, value }, payload }) {
      return setIn(state, ['registeredFields', field, 'selectedRow'], value);
    },
    [CUSTOM_VALIDATION](state, { payload }) {
      let result = state;
      if (payload && payload.field) {
        result = deleteIn(result, ['customValidationErrors', payload.field]);
        result = setIn(result, ['customValidationErrors', payload.field], payload.error);
      }
      return result;
    },
    [BUSY_ON_TEMPORARY](state, { meta: { mutationId } }) {
      let result = state;
      result = setIn(result, 'busy', true);
      result = setIn(result, 'busyMutationId', mutationId);
      return result;
    },
    [TEMPORARY_SAVE_SUCCEEDED](state, { meta: { mutationId, ignoreMutationIdCheck }, payload }) {
      let result = state;

      if (state.busyMutationId === mutationId) {
        result = setIn(result, `busy`, false);
        result = setIn(result, `busyMutationId`, null);
      }

      // Any currently visible modals should block temporary saves resolving
      // (Example issue with Print modals randomly closing)
      if (state.values?.entityMetaData?.actions?.length > 0) {
        console.warn('Temporary save discarded - Outstanding actions detected.');
        return result;
      }

      if (!ignoreMutationIdCheck && state.mutationId !== mutationId) {
        console.warn('Temporary save discarded - Mutation id check failed.');
        return result;
      }
      if (
        payload.values &&
        payload.values.uuid === result.values?.uuid &&
        payload.values.concurrencyVersion === result.values?.concurrencyVersion &&
        result.registeredFields
      ) {
        result = setIn(result, 'values', mergeTemporarySaveResult(payload, result.values));
      } else {
        console.warn('Temporary save discarded');
      }
      return result;
    },
    [UPDATE_MUTATION_ID](state, payload) {
      return setIn(state, 'mutationId', getIn(payload, 'payload.mutationId') || payload);
    },
  };

  const reducer = (state = empty, action = {}) => {
    const behavior = behaviors[action.type];
    return behavior ? behavior(state, action) : state;
  };

  const byForm =
    (reducer) =>
    (state = empty, action = {}) => {
      const form = action && action.meta && action.meta.form;
      if (!form || !isFormAction(action)) {
        return state;
      }
      if (action.type === DESTROY) {
        return action.meta.form.reduce((result, form) => deleteInWithCleanUp(result, form), state);
      }
      const formState = getIn(state, form);
      const result = reducer(formState, action);
      return result === formState ? state : setIn(state, form, result);
    };

  /**
   * Adds additional functionality to the reducer
   */
  function decorate(target) {
    target.plugin = function plugin(reducers) {
      // Use 'function' keyword to enable 'this'
      return decorate((state = empty, action = {}) =>
        Object.keys(reducers).reduce((accumulator, key) => {
          const previousState = getIn(accumulator, key);
          const nextState = reducers[key](previousState, action, getIn(state, key));
          return nextState === previousState ? accumulator : setIn(accumulator, key, nextState);
        }, this(state, action))
      );
    };

    return target;
  }

  return decorate(byForm(reducer));
};

export default createFormReducer;
